import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Auth from './pages/Auth';
import CompleteProfilePhone from './pages/CompleteProfilePhone';
import Home from './pages/Home';
import Service from './pages/Service';
import ServiceCatalog from './pages/ServiceCatalog';
import WorkerLanding from './pages/WorkerLanding';
import MyBookings from './pages/MyBookings';
import Profile from './pages/Profile';
import UserDashboard from './pages/UserDashboard';
import Admin from './pages/Admin';
import Workers from './pages/Workers';
import AdminBookings from './pages/AdminBookings';
import Chat from './pages/Chat';
import SuperAdmin from './pages/SuperAdmin';
import FieldOperator from './pages/FieldOperator';
import RegionLeadDashboard from './pages/RegionLeadDashboard';
import MasonDashboardPage from './pages/MasonDashboardPage';
import WorkerDashboard from './pages/worker/WorkerDashboard';
import OpenWork from './pages/worker/OpenWork';
import FutureWork from './pages/worker/FutureWork';
import WorkerProfile from './pages/worker/WorkerProfile';
import WorkerSupport from './pages/worker/WorkerSupport';
import WorkerMap from './pages/worker/WorkerMap';
import WorkHistory from './pages/worker/WorkHistory';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import SocialLanding from './pages/SocialLanding';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsPolicy from './pages/TermsPolicy';
import Help from './pages/Help';
import { LocationProvider } from './context/LocationContext';
import { ToastProvider } from './context/ToastContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';

const PROJECT_BASE_PATH = '/Gigtos';

function normalizeProjectRouteForHashRouter() {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  if (hash && hash.startsWith('#/')) return;
  const basePath = PROJECT_BASE_PATH;
  const isProjectPath =
    pathname === basePath ||
    pathname === `${basePath}/` ||
    pathname.startsWith(`${basePath}/`);
  if (!isProjectPath) return;
  const routePath = pathname.slice(basePath.length) || '/';
  if (routePath === '/' && !search) return;
  window.history.replaceState(null, '', `${basePath}/#${routePath}${search}`);
}

// ── ProtectedRoute ──────────────────────────────────────────────────────────
// Defined OUTSIDE App so React does not create a new component class on every
// render. Reads auth state from the shared AuthContext via useAuth().
function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireWorker = false,
}) {
  const { user, isAdmin, isSuperAdmin, isWorker } = useAuth();
  if (!user) return <Navigate to="/auth" />;
  if (requireSuperAdmin && !isSuperAdmin) return <Navigate to="/" />;
  if (requireAdmin && !isAdmin) return <Navigate to="/" />;
  if (requireWorker && !isWorker) return <Navigate to="/" />;
  return children;
}

// ── AppContent ──────────────────────────────────────────────────────────────
// Also outside App for stable identity. Reads auth state from AuthContext.
function AppContent() {
  const { user, isAdmin, isSuperAdmin, isRegionLead, isWorker, adminRole, loading } = useAuth();
  const location = useLocation();
  const isAuthRoute = location.pathname === '/auth';

  const getAdminRedirect = () => {
    if (isSuperAdmin) return '/admin/super';
    if (adminRole === 'field_operator') return '/operator';
    if (isRegionLead) return '/admin/region-lead';
    return '/admin/bookings';
  };

  const getPostLoginRedirect = () => {
    if (isAdmin) return getAdminRedirect();
    if (isWorker) return '/worker/dashboard';
    return '/';
  };

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        color: '#666',
      }}>
        ⏳ Loading...
      </div>
    );
  }

  return (
    <>
      {!isAuthRoute && <Header />}
      <main style={{ minHeight: isAuthRoute ? '100vh' : '70vh' }}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={user ? (isAdmin || isWorker ? <Navigate to={getPostLoginRedirect()} /> : <Home />) : <Home />}
          />
          <Route path="/auth" element={user ? <Navigate to={getPostLoginRedirect()} /> : <Auth />} />
          <Route path="/services" element={<ServiceCatalog />} />
          <Route path="/workers" element={<WorkerLanding />} />
          <Route path="/social" element={<SocialLanding />} />
          <Route path="/join" element={<SocialLanding />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsPolicy />} />
          <Route path="/help" element={<Help />} />

          {/* Protected User Routes */}
          <Route path="/service" element={<ProtectedRoute><Service /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/complete-profile-phone" element={<ProtectedRoute><CompleteProfilePhone /></ProtectedRoute>} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          {/* Ride vertical disabled for MVP — driver app and assignment flow not complete */}
          <Route path="/ride-booking" element={<Navigate to="/services" replace />} />
          <Route path="/ride-tracking/:rideId" element={<Navigate to="/services" replace />} />

          {/* Protected Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
          <Route path="/admin/workers" element={<ProtectedRoute requireAdmin><Workers /></ProtectedRoute>} />
          <Route path="/admin/bookings" element={<ProtectedRoute requireAdmin><AdminBookings /></ProtectedRoute>} />
          <Route path="/admin/region-lead" element={<ProtectedRoute requireAdmin><RegionLeadDashboard /></ProtectedRoute>} />
          <Route path="/mason/dashboard" element={<ProtectedRoute requireAdmin><MasonDashboardPage /></ProtectedRoute>} />
          <Route path="/operator" element={<ProtectedRoute requireAdmin><FieldOperator /></ProtectedRoute>} />

          {/* Protected Worker Routes */}
          <Route path="/worker/dashboard" element={<ProtectedRoute requireWorker><WorkerDashboard /></ProtectedRoute>} />
          <Route path="/worker/open-work" element={<ProtectedRoute requireWorker><OpenWork /></ProtectedRoute>} />
          <Route path="/worker/future-work" element={<ProtectedRoute requireWorker><FutureWork /></ProtectedRoute>} />
          <Route path="/worker/profile" element={<ProtectedRoute requireWorker><WorkerProfile /></ProtectedRoute>} />
          <Route path="/worker/support" element={<ProtectedRoute requireWorker><WorkerSupport /></ProtectedRoute>} />
          <Route path="/worker/map" element={<ProtectedRoute requireWorker><WorkerMap /></ProtectedRoute>} />
          <Route path="/worker/history" element={<ProtectedRoute requireWorker><WorkHistory /></ProtectedRoute>} />

          {/* Protected SuperAdmin Route */}
          <Route path="/admin/super" element={<ProtectedRoute requireSuperAdmin><SuperAdmin /></ProtectedRoute>} />
        </Routes>
      </main>
      {!isAuthRoute && <Footer />}
    </>
  );
}

function App() {
  normalizeProjectRouteForHashRouter();

  return (
    <HashRouter>
      <AuthProvider>
        <ToastProvider>
          <LanguageProvider>
            <LocationProvider>
              <AppContent />
            </LocationProvider>
          </LanguageProvider>
        </ToastProvider>
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
